import { isUpgradeCellValuePresent } from "app/pages/sales-intelligence/common-components/table-cells";
import { selectActiveLeadsType, selectActiveSearchResultsDto } from "app/pages/sales-intelligence/modules/leads-search";
import type {
    AddTopNLeadsParams,
    FetchTopNDomainsFn,
    MoveTopNLeadsParams,
} from "app/pages/sales-intelligence/modules/leads-search/store/types";
import { selectCountriesFilterValue } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { updateListOpportunitiesAsync } from "app/pages/sales-intelligence/sub-modules/opportunities/store/action-creators";
import {
    moveOpportunitiesListWithSuccessModalThunk,
    updateListOpportunitiesWithSuccessModalThunk,
} from "app/pages/sales-intelligence/sub-modules/opportunities/store/effects";
import type { OpportunityListType } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

export const addLeadsToListThunk =
    (list: OpportunityListType, domains: readonly string[]) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const state = getState();
        const countriesFilterValue = selectCountriesFilterValue(state);

        const dto = { opportunities: domains, countries: countriesFilterValue ?? [] };

        await dispatch(updateListOpportunitiesWithSuccessModalThunk(list, dto));
    };

export const createAddTopNLeadsToListThunk =
    <T extends AddTopNLeadsParams>(fetchDomains: FetchTopNDomainsFn<T>) =>
    (params: T) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        dispatch(updateListOpportunitiesAsync.request());

        const state = getState();
        const activeLeadsType = selectActiveLeadsType(state);
        const searchRequestDto = selectActiveSearchResultsDto(state);

        const domains = await fetchDomains({
            ...params,
            dto: searchRequestDto,
            leadsType: activeLeadsType,
        });
        const validDomains = domains?.filter((x) => !isUpgradeCellValuePresent(x));

        if (!validDomains?.length) {
            dispatch(updateListOpportunitiesAsync.failure("No domains available to add"));
            return;
        }

        const countriesFilterValue = selectCountriesFilterValue(state);

        const dto = { opportunities: validDomains, countries: countriesFilterValue ?? [] };

        return dispatch(updateListOpportunitiesWithSuccessModalThunk(params.list, dto));
    };

export const moveLeadsToListThunk =
    (currentList: OpportunityListType, targetList: OpportunityListType, domains: readonly string[]) =>
    (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const state = getState();
        const shouldIncludeRegionals = selectActiveLeadsType(state) === "companies";

        const dto = { moveToOpportunityListId: targetList.opportunityListId, domains };

        return dispatch(moveOpportunitiesListWithSuccessModalThunk(currentList, dto, shouldIncludeRegionals));
    };

export const createMoveTopNLeadsToListThunk =
    <T extends MoveTopNLeadsParams>(fetchDomains: FetchTopNDomainsFn<T>) =>
    (params: T) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        dispatch(updateListOpportunitiesAsync.request());

        const state = getState();
        const activeLeadsType = selectActiveLeadsType(state);
        const searchRequestDto = selectActiveSearchResultsDto(state);

        const domains = await fetchDomains({
            ...params,
            dto: searchRequestDto,
            leadsType: activeLeadsType,
        });

        if (!domains?.length) {
            dispatch(updateListOpportunitiesAsync.failure("No domains available to move"));
            return;
        }

        const includeRegionals = selectActiveLeadsType(state) === "companies";

        const dto = {
            moveToOpportunityListId: params.targetList.opportunityListId,
            domains,
        };

        return dispatch(moveOpportunitiesListWithSuccessModalThunk(params.currentList, dto, includeRegionals));
    };
